import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Tooltip,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import { ArrowLeft, Pencil, Eye, FileText, Trash2, Download, RefreshCw } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { formEndpoints } from '../../services/api/endpoints/form.endpoint';
import { fileEndpoints } from '../../services/api/endpoints/file.endpoint';
import { Form, FormSubmission } from '../../types/form';
import { FormPreviewDialog } from '../../components/forms';
import { ROUTES } from '../../config/constants/routes';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS } from '../../config/constants';
import { logger } from '../../utils/logger';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import Notifications from '../../components/common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';
import EmptyStateIcon from '../../assets/images/emptyState-illus.svg';

// Format date for display
const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

// Get field label from form fields
const getFieldLabel = (fieldId: string, form: Form | null): string => {
  if (!form?.fields) return fieldId;
  const field = form.fields.find(f => f.fieldId === fieldId);
  return field?.label || fieldId;
};

// Whether a submission column belongs to a file upload field
const isFileField = (fieldId: string, form: Form | null): boolean => {
  return form?.fields?.find(f => f.fieldId === fieldId)?.fieldType === 'file';
};

// File field values are JSON arrays of S3 keys ("forms/{formId}/{uuid}-{name}")
const parseFileKeys = (value: string | undefined): string[] | null => {
  if (!value) return null;
  try {
    const keys = JSON.parse(value);
    if (Array.isArray(keys) && keys.every(k => typeof k === 'string')) {
      return keys;
    }
  } catch {
    // not a JSON array — fall through
  }
  return null;
};

// Display name: basename with the leading upload uuid stripped
const getFileDisplayName = (fileKey: string): string => {
  const baseName = fileKey.split('/').pop() || fileKey;
  return baseName.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i, '');
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const FormDetailView = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { formId } = useParams<{ formId: string }>();
  const { role } = useAuth();

  const [form, setForm] = useState<Form | null>(null);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: 'deleteAll' | 'deleteSingle';
    uuid?: string;
  }>({
    open: false,
    type: 'deleteAll',
  });

  // Notification State
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );

  // Fetch form details
  useEffect(() => {
    const fetchForm = async () => {
      if (!formId || !selectedOrganizationId) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await formEndpoints.getForm(selectedOrganizationId, formId);
        setForm(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch form details';
        setError(message);
        logger.error('Error fetching form:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchForm();
  }, [formId, selectedOrganizationId]);

  // Fetch form submissions when form is loaded
  useEffect(() => {
    const fetchSubmissions = async () => {
      if (!form?.formId || !selectedOrganizationId) return;

      setIsLoadingSubmissions(true);

      try {
        const data = await formEndpoints.getFormValues(selectedOrganizationId, form.formId);
        setSubmissions(data);
      } catch (err) {
        logger.error('Error fetching form submissions:', err);
        // Don't show error for submissions - just show empty state
      } finally {
        setIsLoadingSubmissions(false);
      }
    };

    fetchSubmissions();
  }, [form?.formId, selectedOrganizationId]);

  const handleRefreshSubmissions = async () => {
    if (!form?.formId || !selectedOrganizationId) return;

    setIsLoadingSubmissions(true);

    try {
      const data = await formEndpoints.getFormValues(selectedOrganizationId, form.formId);
      setSubmissions(data);
      setNotification({
        open: true,
        message: 'Submissions refreshed',
        severity: SEVERITY.SUCCESS,
      });
    } catch (err) {
      setNotification({
        open: true,
        message: 'Failed to refresh submissions',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setIsLoadingSubmissions(false);
    }
  };

  const handleDeleteAllSubmissions = async () => {
    if (!form?.formId || !selectedOrganizationId) return;

    try {
      await formEndpoints.deleteFormValues(selectedOrganizationId, form.formId);
      setSubmissions([]);
      setNotification({
        open: true,
        message: 'All submissions deleted successfully',
        severity: SEVERITY.SUCCESS,
      });
    } catch (err) {
      setNotification({
        open: true,
        message: 'Failed to delete submissions',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setConfirmDialog({ open: false, type: 'deleteAll' });
    }
  };

  const handleDeleteSingleSubmission = async () => {
    if (!form?.formId || !selectedOrganizationId || !confirmDialog.uuid) return;

    try {
      await formEndpoints.deleteFormValue(selectedOrganizationId, form.formId, confirmDialog.uuid);
      setSubmissions(prev => prev.filter(sub => sub.uuid !== confirmDialog.uuid));
      setNotification({
        open: true,
        message: 'Submission deleted successfully',
        severity: SEVERITY.SUCCESS,
      });
    } catch (err) {
      setNotification({
        open: true,
        message: 'Failed to delete submission',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setConfirmDialog({ open: false, type: 'deleteAll' });
    }
  };

  const handleConfirmDelete = () => {
    if (confirmDialog.type === 'deleteAll') {
      handleDeleteAllSubmissions();
    } else if (confirmDialog.type === 'deleteSingle') {
      handleDeleteSingleSubmission();
    }
  };

  const handleDownloadSubmissionFile = async (fileKey: string) => {
    if (!selectedOrganizationId) return;
    try {
      const downloadUrl = await fileEndpoints.downloadFile(selectedOrganizationId, fileKey);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = getFileDisplayName(fileKey);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      logger.error('Error downloading submission file:', err);
      setNotification({
        open: true,
        message: 'Failed to download file',
        severity: SEVERITY.ERROR,
      });
    }
  };

  // Render a submission cell value — file fields render as download links
  const renderSubmissionValue = (fieldKey: string, value: string | undefined) => {
    if (isFileField(fieldKey, form)) {
      const fileKeys = parseFileKeys(value);
      if (fileKeys && fileKeys.length > 0) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {fileKeys.map(fileKey => (
              <Link
                key={fileKey}
                component="button"
                type="button"
                variant="body2"
                onClick={() => handleDownloadSubmissionFile(fileKey)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textAlign: 'left',
                }}
              >
                <Download size={14} />
                {getFileDisplayName(fileKey)}
              </Link>
            ))}
          </Box>
        );
      }
    }
    return <Typography variant="body2">{value || '-'}</Typography>;
  };

  const handleExportCSV = () => {
    if (!submissions.length || !form) return;

    // Get all unique field keys from submissions
    const allFieldKeys = new Set<string>();
    submissions.forEach(sub => {
      if (sub.formValues) {
        Object.keys(sub.formValues).forEach(key => allFieldKeys.add(key));
      }
    });
    const fieldKeys = Array.from(allFieldKeys);

    // Build CSV header
    const headers = [
      'Submitted At',
      'Customer Name',
      'Contact',
      ...fieldKeys.map(key => getFieldLabel(key, form)),
    ];

    // Build CSV rows
    const rows = submissions.map(sub => {
      const values = [
        formatDate(sub.createdAt),
        sub.customerName || '-',
        sub.contact || '-',
        ...fieldKeys.map(key => {
          const rawValue = sub.formValues?.[key];
          if (isFileField(key, form)) {
            const fileKeys = parseFileKeys(rawValue);
            if (fileKeys && fileKeys.length > 0) {
              return fileKeys.map(getFileDisplayName).join('; ');
            }
          }
          return rawValue || '-';
        }),
      ];
      // Escape values for CSV
      return values.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${form.formName.replace(/\s+/g, '_')}_submissions.csv`;
    link.click();
  };

  const handleBack = () => {
    navigate(ROUTES.FORMS);
  };

  const handleEdit = () => {
    if (form) {
      const identifier = form.uuid || form.formId;
      navigate(ROUTES.FORMS_EDIT.replace(':formId', identifier));
    }
  };

  const handlePreview = () => {
    setPreviewOpen(true);
  };

  const isAdmin = role !== AUTH_CONSTANTS.ROLES.USER;

  if (!selectedOrganizationId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select an organization to view form details.</Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!form) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Form not found.</Alert>
      </Box>
    );
  }

  // Get all unique field keys from submissions for dynamic columns
  const allFieldKeys = new Set<string>();
  submissions.forEach(sub => {
    if (sub.formValues) {
      Object.keys(sub.formValues).forEach(key => allFieldKeys.add(key));
    }
  });
  const fieldKeys = Array.from(allFieldKeys);

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={handleBack}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          Dynamic Forms
        </Link>
        <Typography color="text.primary">{form.formName}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <IconButton onClick={handleBack} sx={{ mt: 0.5 }}>
            <ArrowLeft size={20} />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <FileText size={24} color={theme.palette.primary.main} />
              <Typography variant="h5" sx={{ fontWeight: 500 }}>
                {form.formName}
              </Typography>
              <Chip
                label={form.isActive !== false ? 'Active' : 'Inactive'}
                size="small"
                color={form.isActive !== false ? 'success' : 'default'}
              />
            </Box>
            {form.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 5 }}>
                {form.description}
              </Typography>
            )}
          </Box>
        </Box>

        {isAdmin && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Preview Form">
              <IconButton onClick={handlePreview}>
                <Eye size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Form">
              <IconButton onClick={handleEdit} color="primary">
                <Pencil size={20} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Form Info Summary */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Trigger Keywords
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {form.triggerPrompt ? (
                form.triggerPrompt.split(',').map((keyword, idx) => (
                  <Chip
                    key={idx}
                    label={keyword.trim()}
                    size="small"
                    sx={{
                      backgroundColor: `${theme.palette.primary.main}10`,
                      color: theme.palette.primary.main,
                    }}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No keywords
                </Typography>
              )}
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total Fields
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {form.fields?.length || 0}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total Submissions
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {submissions.length}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Form ID
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {form.formId}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label={`Submissions (${submissions.length})`} />
          <Tab label={`Form Structure (${form.fields?.length || 0} fields)`} />
        </Tabs>
      </Box>

      {/* Submissions Tab */}
      <TabPanel value={tabValue} index={0}>
        {/* Actions Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshCw size={16} />}
            onClick={handleRefreshSubmissions}
            disabled={isLoadingSubmissions}
          >
            Refresh
          </Button>
          {submissions.length > 0 && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Download size={16} />}
                onClick={handleExportCSV}
              >
                Export CSV
              </Button>
              {isAdmin && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<Trash2 size={16} />}
                  onClick={() => setConfirmDialog({ open: true, type: 'deleteAll' })}
                >
                  Delete All
                </Button>
              )}
            </>
          )}
        </Box>

        {isLoadingSubmissions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : submissions.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <img className="w-24 mx-auto mb-3" src={EmptyStateIcon} alt="No submissions" />
            <Typography variant="h6" gutterBottom>
              No submissions yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Form submissions will appear here when users fill out this form through the chat.
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={0}>
            <TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        px: 3,
                        py: 1.5,
                        color: theme.palette.text.secondary,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Submitted At
                    </TableCell>
                    <TableCell sx={{ py: 1.5, color: theme.palette.text.secondary }}>
                      Customer
                    </TableCell>
                    <TableCell sx={{ py: 1.5, color: theme.palette.text.secondary }}>
                      Contact
                    </TableCell>
                    {fieldKeys.map(key => (
                      <TableCell key={key} sx={{ py: 1.5, color: theme.palette.text.secondary }}>
                        {getFieldLabel(key, form)}
                      </TableCell>
                    ))}
                    {isAdmin && (
                      <TableCell sx={{ py: 1.5, color: theme.palette.text.secondary, width: 60 }}>
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.map(submission => (
                    <TableRow
                      key={submission.uuid}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell sx={{ px: 3, whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">{formatDate(submission.createdAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{submission.customerName || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{submission.contact || '-'}</Typography>
                      </TableCell>
                      {fieldKeys.map(key => (
                        <TableCell key={key}>
                          {renderSubmissionValue(key, submission.formValues?.[key])}
                        </TableCell>
                      ))}
                      {isAdmin && (
                        <TableCell>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                setConfirmDialog({
                                  open: true,
                                  type: 'deleteSingle',
                                  uuid: submission.uuid,
                                })
                              }
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </TabPanel>

      {/* Form Structure Tab */}
      <TabPanel value={tabValue} index={1}>
        {!form.fields || form.fields.length === 0 ? (
          <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
            <Alert severity="info">No fields have been added to this form yet.</Alert>
          </Paper>
        ) : (
          <Paper elevation={0}>
            <TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ px: 3, py: 1.5, color: theme.palette.text.secondary, width: 60 }}
                    >
                      #
                    </TableCell>
                    <TableCell sx={{ py: 1.5, color: theme.palette.text.secondary }}>
                      Field Name
                    </TableCell>
                    <TableCell sx={{ py: 1.5, color: theme.palette.text.secondary }}>
                      Field ID
                    </TableCell>
                    <TableCell sx={{ py: 1.5, color: theme.palette.text.secondary }}>
                      Type
                    </TableCell>
                    <TableCell sx={{ py: 1.5, color: theme.palette.text.secondary }}>
                      Required
                    </TableCell>
                    <TableCell sx={{ py: 1.5, color: theme.palette.text.secondary }}>
                      Options
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...form.fields]
                    .sort((a, b) => a.order - b.order)
                    .map((field, index) => (
                      <TableRow
                        key={field.fieldId}
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell sx={{ px: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            {index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 500 }}>{field.label}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                          >
                            {field.fieldId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={field.fieldType}
                            size="small"
                            sx={{
                              backgroundColor: `${theme.palette.primary.main}10`,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={field.required ? 'Yes' : 'No'}
                            size="small"
                            color={field.required ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {field.fieldType === 'select' &&
                          field.options &&
                          field.options.length > 0 ? (
                            <Box
                              sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 250 }}
                            >
                              {field.options.slice(0, 3).map((option, idx) => (
                                <Chip
                                  key={idx}
                                  label={option.label}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                              {field.options.length > 3 && (
                                <Chip
                                  label={`+${field.options.length - 3}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Success/Error Messages */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Messages
          </Typography>
          <Paper elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary">
                  Success Message
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {form.successMessage || 'No success message configured'}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary">
                  Error Message
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {form.errorMessage || 'No error message configured'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </TabPanel>

      {/* Preview Dialog */}
      {form && (
        <FormPreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} form={form} />
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.type === 'deleteAll' ? 'Delete All Submissions' : 'Delete Submission'}
        message={
          confirmDialog.type === 'deleteAll'
            ? 'Are you sure you want to delete all submissions for this form? This action cannot be undone.'
            : 'Are you sure you want to delete this submission? This action cannot be undone.'
        }
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDialog({ open: false, type: 'deleteAll' })}
      />

      {/* Notifications */}
      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default FormDetailView;
